import * as React from "react";
import { Box, Typography } from "@mui/material";

export default function HtmlInput({
  value: val,
  onChange,
  label = "Email HTML Template",
  placeholder = "<div>Hello @{userName}</div>",
  minRows = 24,
  maxRows = 24, // only affects height (css), not actual row counting
  disabled = false,
}) {
  // const val = value ?? "";
  const setVal = onChange ?? (() => {});
  const taRef = React.useRef(null);
  const gutterRef = React.useRef(null);

  const lineCount = React.useMemo(
    () => Math.max(1, val.split(/\r\n|\r|\n/).length),
    [val]
  );
  const digits = String(lineCount).length;
  const gutterWidthCh = Math.max(2, digits) + 2; // digits + padding

  // keep gutter vertically in sync with textarea scroll
  const onScroll = (e) => {
    if (gutterRef.current) gutterRef.current.scrollTop = e.target.scrollTop;
  };

  // handle Tab -> insert two spaces (change to "\t" if you prefer)
  const onKeyDown = (e) => {
    if (e.key === "Tab") {
      e.preventDefault();
      const el = taRef.current;
      const start = el.selectionStart;
      const end = el.selectionEnd;
      const insert = "  ";
      const next = val.slice(0, start) + insert + val.slice(end);
      setVal(next);
      requestAnimationFrame(() => {
        el.selectionStart = el.selectionEnd = start + insert.length;
      });
    }
  };

  // build the gutter string: "1\n2\n3\n..."
  const gutterText = React.useMemo(
    () => Array.from({ length: lineCount }, (_, i) => i + 1).join("\n"),
    [lineCount]
  );

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
        {label}
      </Typography>

      <Box
        sx={{
          // editor frame
          position: "relative",
          display: "grid",
          gridTemplateColumns: `${gutterWidthCh}ch 1fr`,
          border: (t) => `1px solid ${t.palette.divider}`,
          borderRadius: 1,
          overflow: "hidden",
          "&:focus-within": {
            borderColor: (t) => t.palette.primary.main,
            boxShadow: (t) => `0 0 0 1px ${t.palette.primary.main}20`,
          },
            maxHeight: `calc(${maxRows} * 1.5em + 2.5rem)`, // match textarea height-ish
          backgroundColor: (t) =>
            t.palette.mode === "dark" ? t.palette.background.paper : "#fafafa",
        }}
      >
        {/* Line-number gutter */}
        <Box
          ref={gutterRef}
          aria-hidden="true"
          sx={{
            px: 1,
            py: 1.25,
            borderRight: (t) => `1px solid ${t.palette.divider}`,
            color: (t) => (t.palette.mode === "dark" ? "#888" : "#999"),
            textAlign: "right",
            fontFamily:
              'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono","Courier New", monospace',
            fontSize: 14,
            lineHeight: 1.5,
            whiteSpace: "pre",
            userSelect: "none",
            pointerEvents: "none",
            overflow: "hidden",
            maxHeight: `calc(${maxRows} * 1.5em + 2.5rem)`, // match textarea height-ish
          }}
        >
          {gutterText}
        </Box>

        {/* Textarea */}
        <Box sx={{ position: "relative" }}>
          <textarea
            ref={taRef}
            defaultValue={val}
            onChange={(e) => setVal(e.target.value)}
            onKeyDown={onKeyDown}
            onScroll={onScroll}
            placeholder={placeholder}
            disabled={disabled}
            rows={minRows}
            style={{
              // reset
              border: 0,
              outline: 0,
              resize: "vertical", // let users enlarge up to maxRows-ish visually
              boxSizing: "border-box",
              width: "100%",
              background: "transparent",
              // typography
              fontFamily:
                'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono","Courier New", monospace',
              fontSize: 14,
              lineHeight: 1.5,
              // spacing
              padding: "1rem 1rem 1rem 1rem",
              // behavior
              whiteSpace: "pre", // no soft wrap so line numbers stay aligned
              overflow: "auto",
              maxHeight: `calc(${minRows} * 1.5em + 2.5rem)`,
            }}
            spellCheck={false}
            wrap="off"
          />
        </Box>
      </Box>

      <Typography
        variant="caption"
        sx={{ mt: 0.75, display: "block", opacity: 0.75 }}
      >
        Placeholders like <code>@{"{var}"}</code> or{" "}
        <code>${"{var}"}</code> are fine.
      </Typography>
    </Box>
  );
}
